import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface FlatPanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, FlatPanelCopy> = {
  en: {
    eyebrow: "Flat Screen Panels • Resistance Welded Deck",
    title: "Flat Panel Wedge Wire Screens",
    lead: "Engineered stainless steel flat screen panels built with continuous parallel V-shaped surface wires resistance-welded perpendicularly across rigid support bars. Designed for maximum vibration resistance, high planar stiffness, and uniform slurry dewatering.",
    points: ["Rigid orthogonal profile wire construction maintains precise slot openings under heavy gravitational and vibrational loads.", "V-shaped wire profile widens inwardly to facilitate rapid liquid release and prevent particle blinding or lodging.", "Custom perimeter frame options include welded flat bars, angled flanges, rubber gaskets, and side clamping channels."],
    facts: [{
      label: "Slot Width",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Construction",
      value: "Planar Resistance-Welded"
    }, {
      label: "Standard Sizes",
      value: "Up to 1,500 × 3,000 mm"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Stainless steel flat panel wire mesh screen with reinforced frame"
  },
  zh: {
    eyebrow: "平板筛板 • 电阻焊接高刚度筛面",
    title: "不锈钢平板楔形丝筛板",
    lead: "采用连续平行 V 型截面楔形绕丝与高刚度支撑筋精密正交电阻焊接而成的不锈钢平板筛网。专为抵抗机械振动、承受高负荷物料冲击并在严苛工业分选流程中保持平整度与精确缝隙而设计。",
    points: ["正交高刚度全熔阻焊结构，在高振动及重度重力载荷下始终保持均一稳定的筛缝开孔精度。", "向后扩张的倒 V 型梯形缝隙截面促进液体快速剥离排出，杜绝固体颗粒在筛缝内部卡堵结垢。", "支持多样化边框加工，包括全焊扁钢边框、角钢安装法兰、聚氨酯橡胶密封条及侧边压紧型材。"],
    facts: [{
      label: "缝隙宽度",
      value: "0.02 – 5.0 mm"
    }, {
      label: "制造结构",
      value: "平板全熔阻焊结构"
    }, {
      label: "标准尺寸",
      value: "最大 1,500 × 3,000 mm"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "带加强边框的不锈钢平板丝网筛板"
  },
  es: {
    eyebrow: "Paneles planos • Malla electrosoldada de alta rigidez",
    title: "Paneles planos de alambre en cuña",
    lead: "Paneles planos de criba fabricados con alambres continuos en V electrosoldados perpendicularmente sobre varillas de soporte rígidas. Diseñados para resistir vibraciones mecánicas y proporcionar deshidratación y clasificación uniforme de pulpas.",
    points: ["Estructura ortogonal electrosoldada que mantiene la abertura de ranura uniforme bajo altas cargas gravitatorias y vibratorias.", "El perfil de alambre en cuña que se ensancha hacia el interior previene el colmatado mecánico y facilita el flujo libre.", "Opciones de marco perimetral soldado, bridas en ángulo, juntas elastoméricas y perfiles de sujeción lateral."],
    facts: [{
      label: "Ancho de ranura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Construcción",
      value: "Plana electrosoldada"
    }, {
      label: "Dimensiones estándar",
      value: "Hasta 1.500 × 3.000 mm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Panel filtrante plano de malla metálica de acero inoxidable con marco reforzado"
  },
  de: {
    eyebrow: "Flachsiebpaneele • Widerstandsgeschweißter Siebbelag",
    title: "Flachpaneele aus Spaltsieb-Profildrähten",
    lead: "Präzisions-Flachsiebplatten aus parallelen V-förmigen Spaltdrähten, die orthogonal auf starren Querstäben widerstandsverschweißt sind. Entwickelt für hohe Schwingfestigkeit, planare Steifigkeit und zuverlässige Suspensionsentwässerung.",
    points: ["Orthogonale Schweißkonstruktion gewährleistet dauerhaft präzise Spaltweiten auch unter extremen Vibrations- und Schwerkraftlasten.", "Sich nach hinten öffnender V-Draht-Querschnitt verhindert Verstopfen und gewährleistet kontinuierliche Selbstreinigung.", "Rahmeneinfassungen nach Maß mit Flachprofilen, Winkelflanschen, Dichtungsauflagen und seitlichen Klemmleisten."],
    facts: [{
      label: "Spaltweite",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Konstruktion",
      value: "Planar widerstandsverschweißt"
    }, {
      label: "Standardmaße",
      value: "Bis zu 1.500 × 3.000 mm"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Edelstahl-Flachsieb mit verstärkter Randeinfassung"
  },
  fr: {
    eyebrow: "Panneaux plats • Grille soudée par résistance",
    title: "Panneaux filtrants plats en fil profilé",
    lead: "Panneaux plats en acier inoxydable constitués de fils en V continus soudés par résistance sur des barres supports transversales rigides. Conçus pour résister aux vibrations intenses et assurer un égouttage et un calibrage de pulpes uniforme.",
    points: ["Rigidité orthogonale exceptionnelle préservant la précision de la fente sous fortes contraintes mécaniques et vibratoires.", "Profil en V s'évasant vers l'arrière pour éliminer les risques de colmatage et accélérer l'évacuation des liquides.", "Cadres perimétriques personnalisés avec fers plats soudés, cornières de fixation et profilés de serrage latéral."],
    facts: [{
      label: "Largeur de fente",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Construction",
      value: "Plane mécano-soudée"
    }, {
      label: "Dimensions standard",
      value: "Jusqu'à 1 500 × 3 000 mm"
    }, {
      label: "Nuances d'alliage",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Panneau filtrant plat en toile métallique inox avec cadre renforcé"
  },
  pt: {
    eyebrow: "Painéis planos • Malha soldada por resistência",
    title: "Painéis planos de tela de arame em cunha",
    lead: "Painéis planos de tela de aço inoxidável constituídos por arames contínuos em perfil V soldados por resistência sobre barras de suporte transversais. Projetados para máxima resistência vibratória, estabilidade planar e desaguamento uniforme.",
    points: ["Construção ortogonal soldada que mantém a abertura de fenda constante mesmo sob vibração contínua e carga pesada.", "Abertura em perfil V alargada para o interior previne o entupimento de partículas e assegura fluxo desobstruído.", "Bordas e molduras customizadas com barras chatas, cantoneiras flangeadas e barras de fixação lateral."],
    facts: [{
      label: "Largura de fenda",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Construção",
      value: "Plana soldada por resistência"
    }, {
      label: "Dimensões padrão",
      value: "Até 1.500 × 3.000 mm"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Painel filtrante plano de tela de aço inoxidável com moldura reforçada"
  }
};
export default function FlatPanelGroup() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="flat-panel" href="/FlatPanelScreensPage" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
    alt: copy.imageAlt
  }} tone="background" />;
}